import React, { useState } from "react";
import { api } from "@/lib/api";
import { PrimaryButton, GhostButton, Card } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { toast } from "sonner";

export default function CandidateScoreModal({ open, candidateIds, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [result, setResult] = useState(null);
  if (!open) return null;

  const count = candidateIds.length;

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/candidates/batch-score", {
        candidate_ids: candidateIds,
        force_rescore: force,
      });
      setResult(r.data);
      const d = r.data;
      if (d.scored > 0) toast.success(`Scored ${d.scored} · skipped ${d.skipped} · failed ${d.failed}`);
      else if (d.skipped > 0 && d.failed === 0) toast.message(`All ${d.skipped} already scored (use Force rescore to re-run)`);
      else toast.error(`Scored 0 · skipped ${d.skipped} · failed ${d.failed}`);
      onDone?.(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scoring failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8" data-testid="candidate-score-modal">
      <Card className="max-w-xl w-full max-h-[85vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Discovery · AI Triage</div>
            <h2 className="font-heading text-xl tracking-tighter">AI Score Candidates</h2>
            <p className="text-xs text-zinc-500 mt-1">Claude Sonnet 4.5 will prioritize the selected candidates. This does not approve or import anything.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="candidate-score-close">×</button>
        </div>

        {!result && (
          <>
            <div className="border border-white/10 p-3 text-xs text-zinc-300" data-testid="score-selected-count">
              <span className="font-heading text-2xl text-[#FFB300]">{count}</span> candidate{count === 1 ? "" : "s"} selected for scoring.
            </div>
            <label className="flex items-center gap-2 mt-3 text-xs text-zinc-400">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="force-rescore-checkbox" />
              Force rescore (re-run even if already scored)
            </label>
            <div className="text-[10px] text-zinc-600 mt-2">By default, candidates already scored are skipped.</div>
            <div className="flex justify-end gap-2 mt-5">
              <GhostButton onClick={onClose} data-testid="score-cancel-btn">Cancel</GhostButton>
              <PrimaryButton onClick={run} disabled={busy || count === 0} data-testid="score-confirm-btn">
                {busy ? "Scoring…" : `Score ${count}`}
              </PrimaryButton>
            </div>
            {busy && <div className="mt-3 text-[11px] text-[#FFB300] animate-pulse" data-testid="score-progress">Running AI scoring — this can take a few seconds per candidate…</div>}
          </>
        )}

        {result && (
          <div data-testid="score-result-summary">
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["requested", "Requested", "text-zinc-300"], ["scored", "Scored", "text-[#00E676]"], ["skipped", "Skipped", "text-[#FFB300]"], ["failed", "Failed", "text-[#FF3B30]"]].map(([k, label, cls]) => (
                <div key={k} className="border border-white/10 p-2">
                  <div className={`font-heading text-2xl ${cls}`} data-testid={`score-summary-${k}`}>{result[k] ?? 0}</div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
                </div>
              ))}
            </div>
            <div className="border border-white/10 mt-3 max-h-[40vh] overflow-y-auto divide-y divide-white/5">
              {result.results.map((r, i) => (
                <div key={r.candidate_id || i} className="flex items-center justify-between gap-3 px-3 py-2 text-[11px]" data-testid={`score-result-row-${i}`}>
                  <span className="text-zinc-300 truncate max-w-[220px]">{r.candidate_name || r.candidate_id}</span>
                  <span className="flex items-center gap-2">
                    {r.ai_score_label && <StatusPill value={r.ai_score_label} />}
                    {r.ai_score != null && <span className="font-mono-data text-zinc-400">{r.ai_score}</span>}
                    <span className={r.status === "scored" ? "text-[#00E676]" : r.status === "skipped" ? "text-[#FFB300]" : "text-[#FF3B30]"}>{r.status}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <PrimaryButton onClick={onClose} data-testid="score-done-btn">Done</PrimaryButton>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
